import { z } from 'zod';

/**
 * Canonical enumerations for the Kon Store domain.
 *
 * These are the single source of truth for both the database (mirrored as
 * CHECK constraints in migrations) and the client. Adding a value here and
 * to the migration is the only way to introduce a new category/rarity/status.
 */

/* ------------------------------------------------------------------ */
/* Product category                                                    */
/* ------------------------------------------------------------------ */

export const PRODUCT_CATEGORIES = [
  'seed',
  'pet',
  'gear',
  'currency',
  'bundle',
] as const;

export const productCategorySchema = z.enum(PRODUCT_CATEGORIES);
export type ProductCategory = z.infer<typeof productCategorySchema>;

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  seed: 'Seeds',
  pet: 'Pets',
  gear: 'Gear',
  currency: 'Currency',
  bundle: 'Bundles',
};

/* ------------------------------------------------------------------ */
/* Rarity                                                              */
/* ------------------------------------------------------------------ */

/**
 * Grow a Garden 2 rarity tiers, ordered lowest -> highest. The order matters:
 * it is used for the rarity color scale (grey -> green -> blue -> purple ->
 * gold -> red) and for sorting. See {@link RARITY_META}.
 */
export const RARITIES = [
  'common',
  'uncommon',
  'rare',
  'legendary',
  'mythic',
  'super',
] as const;

export const raritySchema = z.enum(RARITIES);
export type Rarity = z.infer<typeof raritySchema>;

/* ------------------------------------------------------------------ */
/* Order status — state machine                                        */
/* ------------------------------------------------------------------ */

export const ORDER_STATUSES = [
  'CART',
  'PENDING_PAYMENT',
  'PAID',
  'FULFILLED',
  'CANCELLED',
] as const;

export const orderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  CART: 'Cart',
  PENDING_PAYMENT: 'Pending payment',
  PAID: 'Paid',
  FULFILLED: 'Fulfilled',
  CANCELLED: 'Cancelled',
};

/**
 * Allowed transitions of the order state machine. A transition not present in
 * this map is rejected by the repository/route layer.
 *
 * CART -> PENDING_PAYMENT -> PAID -> FULFILLED
 * Any non-terminal state may move to CANCELLED.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  CART: ['PENDING_PAYMENT', 'CANCELLED'],
  PENDING_PAYMENT: ['PAID', 'CANCELLED'],
  PAID: ['FULFILLED', 'CANCELLED'],
  FULFILLED: [],
  CANCELLED: [],
};

export function canTransition(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_TRANSITIONS[from].includes(to);
}

/* ------------------------------------------------------------------ */
/* Currency                                                            */
/* ------------------------------------------------------------------ */

/**
 * `USD` is the base/settlement currency: all `*_cents` columns are stored in
 * USD cents. Other currencies are display-only (the storefront currency
 * selector converts using indicative rates — see shared/money.ts).
 */
export const CURRENCIES = ['USD', 'PHP', 'EUR'] as const;

export const currencySchema = z.enum(CURRENCIES);
export type Currency = z.infer<typeof currencySchema>;

export const BASE_CURRENCY: Currency = 'USD';

/* ------------------------------------------------------------------ */
/* Delivery method                                                     */
/* ------------------------------------------------------------------ */

export const DELIVERY_METHODS = ['in_game_trade', 'account_gift'] as const;

export const deliveryMethodSchema = z.enum(DELIVERY_METHODS);
export type DeliveryMethod = z.infer<typeof deliveryMethodSchema>;

export const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  in_game_trade: 'In-game trade',
  account_gift: 'Account gift',
};
